// src/components/clients/TagChips.tsx
import { useState } from "react";
import { Plus, X } from "lucide-react";
import type { Locale } from "@/shared/i18n/translations";

const labels: Record<string, Record<Locale, string>> = {
  addPlaceholder: {
    en: "Add tag",
    uk: "Додати тег",
    pl: "Dodaj tag",
    ro: "Adaugă etichetă",
  },
  removeTag: {
    en: "Remove {{tag}}",
    uk: "Видалити {{tag}}",
    pl: "Usuń {{tag}}",
    ro: "Elimină {{tag}}",
  },
};

interface Props {
  tags: string[];
  locale: Locale;
  onChange: (tags: string[]) => Promise<void>;
}

export function TagChips({ tags, locale, onChange }: Props) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const t = (key: keyof typeof labels) => labels[key][locale] ?? labels[key].en;

  const commit = async (next: string[]) => {
    try {
      await onChange(next);
    } catch (err) {
      console.error("TagChips save failed:", err);
    }
  };

  const remove = (tag: string) => commit(tags.filter((x) => x !== tag));

  const submit = async () => {
    const v = draft.trim();
    setAdding(false);
    setDraft("");
    if (!v || tags.includes(v)) return;
    await commit([...tags, v]);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex min-h-9 items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-[color-mix(in_srgb,var(--accent)_10%,white)] border border-transparent text-xs text-[var(--accent)]"
        >
          {tag}
          <button
            type="button"
            onClick={() => void remove(tag)}
            aria-label={t("removeTag").replace("{{tag}}", tag)}
            className="relative h-7 w-7 grid place-items-center rounded-full text-[var(--accent)] hover:bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] after:absolute after:-inset-2"
          >
            <X size={12} aria-hidden />
          </button>
        </span>
      ))}
      {adding ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void submit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void submit();
            }
            if (e.key === "Escape") {
              setAdding(false);
              setDraft("");
            }
          }}
          placeholder={t("addPlaceholder")}
          className="min-h-11 px-3 py-1 rounded-full bg-[var(--paper)] border border-[var(--hairline)] text-base sm:text-xs text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] min-w-[120px]"
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="min-h-11 inline-flex items-center gap-1 px-3 py-1 rounded-full border border-dashed border-[var(--hairline)] text-xs text-[var(--muted)] hover:bg-[rgba(12,26,51,0.03)] hover:text-[var(--ink)] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
        >
          <Plus size={12} aria-hidden /> {t("addPlaceholder")}
        </button>
      )}
    </div>
  );
}
